'use client'

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Search, User, FileText, DollarSign, Check, CreditCard, Banknote, Smartphone, Receipt } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

interface Patient {
    id: string
    first_name: string
    last_name: string
    personnummer: string
    latest_appointment?: {
        appointment_date: string
        start_time: string
        doctor_name: string
    }
}

interface ClinicService {
    id: string
    service_type: string
    service_name: string
    base_price: number
    is_active: boolean
    isAvtalFee?: boolean
}

interface AvtalInfo {
    avtal_type: 'vardval' | 'nationella_taxan'
    patient_fee: number
    is_active: boolean
    normalarvode?: number
    visit_supplement?: number
}

interface EnhancedInvoiceWizardProps {
    clinicId: string
    onSuccess: (invoiceId: string) => void
    onCancel: () => void
}

const PATIENTAVGIFT_ID = '__patientavgift__'

export default function EnhancedInvoiceWizard({
    clinicId,
    onSuccess,
    onCancel
}: EnhancedInvoiceWizardProps) {
    const { t } = useTranslation('financial')
    const [step, setStep] = useState(1)
    const [loading, setLoading] = useState(false)

    // Step 1: Patient Selection
    const [patients, setPatients] = useState<Patient[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)

    // Step 2: Services Selection
    const [services, setServices] = useState<ClinicService[]>([])
    const [selectedServices, setSelectedServices] = useState<{ [key: string]: number }>({})
    const [avtalInfo, setAvtalInfo] = useState<AvtalInfo | null>(null)

    // Step 3: Staff Selection
    const [doctors, setDoctors] = useState<any[]>([])
    const [nurses, setNurses] = useState<any[]>([])
    const [selectedDoctor, setSelectedDoctor] = useState('')
    const [selectedNurse, setSelectedNurse] = useState('')

    // Step 3: Company and Insurance Selection
    const [companies, setCompanies] = useState<any[]>([])
    const [insuranceProviders, setInsuranceProviders] = useState<any[]>([])
    const [selectedCompany, setSelectedCompany] = useState('')
    const [selectedInsurance, setSelectedInsurance] = useState('')

    // Payment dialog (shown after invoice creation)
    const [showPaymentDialog, setShowPaymentDialog] = useState(false)
    const [createdInvoiceId, setCreatedInvoiceId] = useState<string | null>(null)
    const [createdInvoiceTotal, setCreatedInvoiceTotal] = useState(0)
    const [paymentMethod, setPaymentMethod] = useState<string>('cash')
    const [cashReceived, setCashReceived] = useState<string>('')
    const [processingPayment, setProcessingPayment] = useState(false)

    // Fetch patients with recent appointments
    useEffect(() => {
        fetchPatientsWithAppointments()
        fetchClinicServices()
        fetchStaff()
        fetchCompaniesAndInsurance()
    }, [clinicId])

    const fetchPatientsWithAppointments = async () => {
        try {
            console.log('🔍 Fetching patients for clinic:', clinicId)

            // Fetch all patients for the clinic
            const response = await fetch(`/api/patients?clinicId=${clinicId}`)
            if (!response.ok) {
                console.error('❌ Failed to fetch patients, status:', response.status)
                return
            }
            const data = await response.json()
            const allPatients = data.data || []
            console.log('✅ Fetched patients:', allPatients.length)

            // Fetch today's appointments
            const today = new Date().toISOString().split('T')[0]
            console.log('📅 Fetching appointments for today:', today)

            try {
                const appointmentsResponse = await fetch(
                    `/api/calendar/appointments?clinic_id=${clinicId}&start_date=${today}&end_date=${today}`
                )

                if (appointmentsResponse.ok) {
                    const appointmentsData = await appointmentsResponse.json()
                    const appointments = appointmentsData.appointments || []
                    console.log('✅ Fetched appointments:', appointments.length)

                    // Create a map of existing patients for quick lookup
                    const patientMap = new Map(allPatients.map((p: any) => [p.id, p]))
                    const combinedPatients = [...allPatients]

                    // Process appointments and ensure all booked patients are in the list
                    appointments.forEach((apt: any) => {
                        if (!apt.patient_id) return

                        let patient = patientMap.get(apt.patient_id)

                        // If patient not in main list, create them from appointment data
                        if (!patient && apt.patient) {
                            console.log('➕ Adding missing patient from appointment:', apt.patient.first_name)
                            patient = {
                                id: apt.patient_id,
                                first_name: apt.patient.first_name || 'Unknown',
                                last_name: apt.patient.last_name || 'Patient',
                                personnummer: '' // Might not be available in appointment data
                            } as Patient
                            combinedPatients.push(patient)
                            patientMap.set(apt.patient_id, patient)
                        }

                        // Add appointment info to patient
                        if (patient) {
                            (patient as Patient).latest_appointment = {
                                appointment_date: apt.appointment_date,
                                start_time: apt.start_time,
                                doctor_name: apt.user?.full_name || 'Doctor'
                            }
                        }
                    })

                    // Sort: patients with appointments today first
                    combinedPatients.sort((a: any, b: any) => {
                        if (a.latest_appointment && !b.latest_appointment) return -1
                        if (!a.latest_appointment && b.latest_appointment) return 1
                        if (a.latest_appointment && b.latest_appointment) {
                            // Sort by appointment time
                            return a.latest_appointment.start_time.localeCompare(b.latest_appointment.start_time)
                        }
                        return 0
                    })

                    console.log('✅ Setting patients with appointments:', combinedPatients.length)
                    setPatients(combinedPatients)
                } else {
                    console.log('⚠️ Appointments API failed, showing all patients')
                    // If appointments API fails, just show all patients
                    setPatients(allPatients)
                }
            } catch (appointmentError) {
                console.log('⚠️ Could not fetch appointments, showing all patients:', appointmentError)
                setPatients(allPatients)
            }
        } catch (error) {
            console.error('❌ Error fetching patients:', error)
        }
    }

    const fetchClinicServices = async () => {
        try {
            const allServices: ClinicService[] = []

            const fetchPrices = async (type: string) => {
                try {
                    const response = await fetch(`/api/financial/clinic-prices?clinicId=${clinicId}&type=${type}`)
                    if (response.ok) {
                        const data = await response.json()
                        return data.prices || []
                    }
                } catch (error) {
                    console.log(`Could not fetch ${type} prices`)
                }
                return []
            }

            // Fetch clinic prices and avtal config in parallel
            const [consultations, labTests, radiology, medications, avtalRes] = await Promise.all([
                fetchPrices('consultation'),
                fetchPrices('lab_test'),
                fetchPrices('radiology'),
                fetchPrices('medication'),
                fetch(`/api/financial/avtal?clinicId=${clinicId}`).then(r => r.ok ? r.json() : { avtals: [] }).catch(() => ({ avtals: [] }))
            ])

            // Find active avtal and extract patient fee
            const avtals = avtalRes.avtals || []
            const activeAvtal = avtals.find((a: any) => a.is_active)
            if (activeAvtal) {
                const info: AvtalInfo = {
                    avtal_type: activeAvtal.avtal_type,
                    patient_fee: activeAvtal.patient_fee || 275,
                    is_active: true,
                    normalarvode: activeAvtal.normalarvode,
                    visit_supplement: activeAvtal.visit_supplement,
                }
                setAvtalInfo(info)

                // Insert Patientavgift as first service
                allServices.push({
                    id: PATIENTAVGIFT_ID,
                    service_type: 'patient_fee',
                    service_name: 'Patientavgift',
                    base_price: info.patient_fee,
                    is_active: true,
                    isAvtalFee: true,
                })

                // Auto-select it
                setSelectedServices(prev => ({ ...prev, [PATIENTAVGIFT_ID]: 1 }))
            }

            consultations.forEach((p: any) => {
                allServices.push({
                    id: p.id,
                    service_type: 'consultation',
                    service_name: p.service_name || 'Consultation',
                    base_price: p.base_price || 0,
                    is_active: p.is_active !== false
                })
            })

            labTests.forEach((p: any) => {
                allServices.push({
                    id: p.id,
                    service_type: 'lab_test',
                    service_name: p.test_name || p.service_name || 'Lab Test',
                    base_price: p.base_price || 0,
                    is_active: p.is_active !== false
                })
            })

            radiology.forEach((p: any) => {
                allServices.push({
                    id: p.id,
                    service_type: 'radiology',
                    service_name: p.service_name || p.exam_type || 'Radiology',
                    base_price: p.base_price || 0,
                    is_active: p.is_active !== false
                })
            })

            medications.forEach((p: any) => {
                allServices.push({
                    id: p.id,
                    service_type: 'medication',
                    service_name: p.medication_name || p.service_name || 'Medication',
                    base_price: p.base_price || 0,
                    is_active: p.is_active !== false
                })
            })

            setServices(allServices.filter(s => s.is_active))
        } catch (error) {
            console.error('Error fetching services:', error)
        }
    }

    const fetchStaff = async () => {
        try {
            const response = await fetch(`/api/clinic/staff?clinicId=${clinicId}`)
            const data = await response.json()
            const staff = data.staff || []

            setDoctors(staff.filter((s: any) => s.role === 'doctor' || s.role === 'physician'))
            setNurses(staff.filter((s: any) => s.role === 'nurse'))
        } catch (error) {
            console.error('Error fetching staff:', error)
        }
    }

    const fetchCompaniesAndInsurance = async () => {
        try {
            // Fetch companies
            const companiesResponse = await fetch(`/api/settings/companies?clinicId=${clinicId}&activeOnly=true`)
            if (companiesResponse.ok) {
                const companiesData = await companiesResponse.json()
                setCompanies(companiesData.companies || [])
            }

            // Fetch insurance providers
            const insuranceResponse = await fetch(`/api/settings/insurance?clinicId=${clinicId}&activeOnly=true`)
            if (insuranceResponse.ok) {
                const insuranceData = await insuranceResponse.json()
                setInsuranceProviders(insuranceData.providers || [])
            }
        } catch (error) {
            console.error('Error fetching companies and insurance:', error)
        }
    }

    // Smart filtering: 
    // - If searching: show all patients matching search
    // - If NOT searching: show only patients with appointments today
    const filteredPatients = searchTerm.trim()
        ? patients.filter(p =>
            p.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.personnummer?.includes(searchTerm)
        )
        : patients.filter(p => p.latest_appointment) // Only show patients with today's appointments

    console.log('👥 Total patients:', patients.length, 'Filtered:', filteredPatients.length, 'Search:', searchTerm, 'Showing:', searchTerm ? 'search results' : 'today\'s appointments')

    const calculateTotal = () => {
        return Object.entries(selectedServices).reduce((sum, [serviceId, quantity]) => {
            const service = services.find(s => s.id === serviceId)
            return sum + (service?.base_price || 0) * quantity
        }, 0)
    }

    const handleCreateInvoice = async () => {
        if (!selectedPatient) return

        setLoading(true)
        try {
            const lineItems = Object.entries(selectedServices).map(([serviceId, quantity]) => {
                const service = services.find(s => s.id === serviceId)!
                return {
                    service_type: service.service_type,
                    description: service.service_name,
                    quantity,
                    unit_price: service.base_price
                }
            })

            const avtalNote = avtalInfo
                ? `Avtal: ${avtalInfo.avtal_type}${avtalInfo.normalarvode ? `, normalarvode: ${avtalInfo.normalarvode}` : ''}${avtalInfo.visit_supplement ? `, besoksersattning: ${avtalInfo.visit_supplement}` : ''}`
                : ''

            const response = await fetch('/api/financial/invoices', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clinicId,
                    patientId: selectedPatient.id,
                    lineItems,
                    responsibleDoctor: selectedDoctor,
                    responsibleNurse: selectedNurse,
                    contractCompanyId: selectedCompany || null,
                    insuranceProviderId: selectedInsurance || null,
                    invoiceDate: new Date().toISOString().split('T')[0],
                    taxRate: 0,
                    notes: avtalNote,
                })
            })

            const data = await response.json()
            if (data.success || data.invoice) {
                const invoiceId = data.invoice?.id || data.id
                const total = data.invoice?.subtotal || data.invoice?.total_amount || calculateTotal()
                setCreatedInvoiceId(invoiceId)
                setCreatedInvoiceTotal(total)
                setShowPaymentDialog(true)
            } else {
                alert('Failed to create invoice: ' + (data.error || 'Unknown error'))
            }
        } catch (error) {
            console.error('Create invoice error:', error)
            alert('Failed to create invoice')
        } finally {
            setLoading(false)
        }
    }

    const handleProcessPayment = async () => {
        if (!createdInvoiceId) return

        setProcessingPayment(true)
        try {
            const response = await fetch('/api/financial/invoices', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invoiceId: createdInvoiceId,
                    status: 'paid',
                    paymentMethod,
                    processedBy: selectedDoctor || null,
                })
            })

            const data = await response.json()
            if (data.success) {
                onSuccess(createdInvoiceId)
            } else {
                alert('Payment failed: ' + (data.error || 'Unknown error'))
            }
        } catch (error) {
            console.error('Payment error:', error)
            alert('Payment processing failed')
        } finally {
            setProcessingPayment(false)
        }
    }

    const handleSkipPayment = () => {
        if (createdInvoiceId) {
            onSuccess(createdInvoiceId)
        }
    }

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {/* Progress Steps */}
            <div className="flex-shrink-0 flex items-center justify-center gap-4 mb-6 pb-4 border-b">
                {[1, 2, 3].map(num => (
                    <div key={num} className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                            {step > num ? <Check className="h-5 w-5" /> : num}
                        </div>
                        {num < 3 && <div className={`w-16 h-1 ${step > num ? 'bg-blue-600' : 'bg-gray-200'}`} />}
                    </div>
                ))}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-2">
                {/* Step 1: Select Patient */}
                {step === 1 && (
                    <div className="space-y-4">
                        <div>
                            <Label>{t('invoices.wizard.searchPatient')}</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder={t('invoices.wizard.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div className="grid gap-2 max-h-[400px] overflow-y-auto">
                            {filteredPatients.map(patient => (
                                <Card
                                    key={patient.id}
                                    className={`cursor-pointer transition-all ${selectedPatient?.id === patient.id ? 'border-blue-600 bg-blue-50' : 'hover:border-gray-400'
                                        }`}
                                    onClick={() => setSelectedPatient(patient)}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{patient.first_name} {patient.last_name}</p>
                                                <p className="text-sm text-gray-600">{patient.personnummer}</p>
                                                {patient.latest_appointment && (
                                                    <p className="text-xs text-green-600 font-medium mt-1">
                                                        📅 Today {patient.latest_appointment.start_time}
                                                        {patient.latest_appointment.doctor_name && ` • ${patient.latest_appointment.doctor_name}`}
                                                    </p>
                                                )}
                                            </div>
                                            {selectedPatient?.id === patient.id && (
                                                <Check className="h-6 w-6 text-blue-600" />
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 2: Select Services */}
                {step === 2 && (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-lg">{t('invoices.wizard.selectServices')}</h3>

                        {/* Patientavgift from avtal -- shown first and highlighted */}
                        {services.filter(s => s.isAvtalFee).map(service => (
                            <div key={service.id} className="p-4 border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-700 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            checked={!!selectedServices[service.id]}
                                            onCheckedChange={(checked) => {
                                                if (checked) {
                                                    setSelectedServices({ ...selectedServices, [service.id]: 1 })
                                                } else {
                                                    const newServices = { ...selectedServices }
                                                    delete newServices[service.id]
                                                    setSelectedServices(newServices)
                                                }
                                            }}
                                        />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-semibold">{service.service_name}</p>
                                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs">
                                                    Fran avtal
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">{service.base_price} SEK</p>
                                            {avtalInfo && (
                                                <p className="text-xs text-blue-600 dark:text-blue-500 mt-0.5">
                                                    {avtalInfo.avtal_type === 'nationella_taxan'
                                                        ? `Nationella Taxan — Region betalar ${(avtalInfo.normalarvode || 0) - service.base_price} SEK`
                                                        : `Vardval${avtalInfo.visit_supplement ? ` — Besoksersattning ${avtalInfo.visit_supplement} SEK` : ''}`
                                                    }
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Other services grouped by type */}
                        {['consultation', 'lab_test', 'radiology', 'medication'].map(type => {
                            const typeServices = services.filter(s => s.service_type === type)
                            if (typeServices.length === 0) return null

                            const typeLabels: Record<string, string> = {
                                consultation: 'Konsultation',
                                lab_test: 'Labbtest',
                                radiology: 'Radiologi',
                                medication: 'Medicin'
                            }

                            return (
                                <div key={type} className="space-y-2">
                                    <h4 className="font-medium">{typeLabels[type] || type}</h4>
                                    <div className="grid gap-2">
                                        {typeServices.map(service => (
                                            <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <Checkbox
                                                        checked={!!selectedServices[service.id]}
                                                        onCheckedChange={(checked) => {
                                                            if (checked) {
                                                                setSelectedServices({ ...selectedServices, [service.id]: 1 })
                                                            } else {
                                                                const newServices = { ...selectedServices }
                                                                delete newServices[service.id]
                                                                setSelectedServices(newServices)
                                                            }
                                                        }}
                                                    />
                                                    <div>
                                                        <p className="font-medium">{service.service_name}</p>
                                                        <p className="text-sm text-gray-600">{service.base_price} SEK</p>
                                                    </div>
                                                </div>
                                                {selectedServices[service.id] && (
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={selectedServices[service.id]}
                                                        onChange={(e) => setSelectedServices({
                                                            ...selectedServices,
                                                            [service.id]: parseInt(e.target.value) || 1
                                                        })}
                                                        className="w-20"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Step 3: Select Staff & Review */}
                {step === 3 && (
                    <div className="space-y-6">
                        <div>
                            <Label>{t('invoices.wizard.doctor')}</Label>
                            <select
                                value={selectedDoctor}
                                onChange={(e) => setSelectedDoctor(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                            >
                                <option value="">{t('invoices.wizard.selectDoctor')}</option>
                                {doctors.map(doc => (
                                    <option key={doc.id} value={doc.id}>
                                        {doc.full_name || doc.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label>{t('invoices.wizard.nurse')}</Label>
                            <select
                                value={selectedNurse}
                                onChange={(e) => setSelectedNurse(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                            >
                                <option value="">{t('invoices.wizard.selectNurse')}</option>
                                {nurses.map(nurse => (
                                    <option key={nurse.id} value={nurse.id}>
                                        {nurse.full_name || nurse.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label>{t('invoices.wizard.company')}</Label>
                            <select
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                            >
                                <option value="">{t('invoices.wizard.none')}</option>
                                {companies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.company_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label>{t('invoices.wizard.insurance')}</Label>
                            <select
                                value={selectedInsurance}
                                onChange={(e) => setSelectedInsurance(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                            >
                                <option value="">{t('invoices.wizard.none')}</option>
                                {insuranceProviders.map(provider => (
                                    <option key={provider.id} value={provider.id}>
                                        {provider.provider_name}
                                        {provider.coverage_percentage > 0 && ` (${t('invoices.wizard.coverage', { pct: provider.coverage_percentage })})`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Invoice Summary */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('invoices.wizard.summary')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-600">{t('invoices.wizard.patient')}</p>
                                    <p className="font-medium">{selectedPatient?.first_name} {selectedPatient?.last_name}</p>
                                </div>

                                <div>
                                    <p className="text-sm text-gray-600 mb-2">{t('invoices.wizard.services')}</p>
                                    {Object.entries(selectedServices).map(([serviceId, quantity]) => {
                                        const service = services.find(s => s.id === serviceId)!
                                        return (
                                            <div key={serviceId} className="flex justify-between text-sm">
                                                <span>{service.service_name} x{quantity}</span>
                                                <span>{service.base_price * quantity} SEK</span>
                                            </div>
                                        )
                                    })}
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex justify-between font-bold text-lg">
                                        <span>{t('invoices.wizard.total')}</span>
                                        <span>{calculateTotal()} SEK</span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">{t('invoices.wizard.vat')}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>

            {/* Payment Dialog -- shown after invoice is created */}
            {showPaymentDialog && (
                <div className="space-y-5">
                    <div className="text-center">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Check className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="font-semibold text-lg">{t('invoices.wizard.created')}</h3>
                        <p className="text-sm text-muted-foreground">
                            {selectedPatient?.first_name} {selectedPatient?.last_name} — {createdInvoiceTotal} SEK
                        </p>
                    </div>

                    <div className="space-y-3">
                        <Label className="font-medium">{t('invoices.wizard.method')}</Label>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { value: 'cash', label: t('invoices.wizard.cash'), icon: Banknote },
                                { value: 'card', label: t('invoices.wizard.card'), icon: CreditCard },
                                { value: 'swish', label: t('invoices.wizard.swish'), icon: Smartphone },
                                { value: 'invoice', label: t('invoices.wizard.invoiceSend'), icon: Receipt },
                            ].map(({ value, label, icon: Icon }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setPaymentMethod(value)}
                                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                                        paymentMethod === value
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    <Icon className={`h-5 w-5 ${paymentMethod === value ? 'text-blue-600' : 'text-gray-500'}`} />
                                    <span className={`font-medium ${paymentMethod === value ? 'text-blue-700 dark:text-blue-300' : ''}`}>{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {paymentMethod === 'cash' && (
                        <div className="space-y-2">
                            <Label>{t('invoices.wizard.received')}</Label>
                            <Input
                                type="number"
                                value={cashReceived}
                                onChange={(e) => setCashReceived(e.target.value)}
                                placeholder={`${createdInvoiceTotal}`}
                            />
                            {cashReceived && parseFloat(cashReceived) > createdInvoiceTotal && (
                                <p className="text-sm font-medium text-green-600">
                                    {t('invoices.wizard.change', { amount: (parseFloat(cashReceived) - createdInvoiceTotal).toFixed(0) })}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        {paymentMethod === 'invoice' ? (
                            <Button className="flex-1" onClick={handleSkipPayment}>
                                <Receipt className="h-4 w-4 mr-2" />
                                Skicka faktura
                            </Button>
                        ) : (
                            <Button
                                className="flex-1"
                                onClick={handleProcessPayment}
                                disabled={processingPayment}
                            >
                                {processingPayment ? 'Behandlar...' : `Registrera betalning — ${createdInvoiceTotal} SEK`}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Navigation Buttons -- hidden during payment dialog */}
            {!showPaymentDialog && (
                <div className="flex-shrink-0 flex justify-between pt-4 border-t mt-4">
                    <Button variant="outline" onClick={step === 1 ? onCancel : () => setStep(step - 1)}>
                        {step === 1 ? 'Cancel' : 'Back'}
                    </Button>
                    <Button
                        onClick={step === 3 ? handleCreateInvoice : () => setStep(step + 1)}
                        disabled={
                            (step === 1 && !selectedPatient) ||
                            (step === 2 && Object.keys(selectedServices).length === 0) ||
                            loading
                        }
                    >
                        {step === 3 ? (loading ? 'Skapar...' : 'Skapa faktura') : 'Nasta'}
                    </Button>
                </div>
            )}
        </div>
    )
}
